// main.js
import React, { Suspense, lazy, useCallback, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PAGE_REDIRECTS } from './pageRoutes';
import { ThemeProvider } from './ThemeContext';
import { AuthProvider, useAuth } from './AuthContext';
import Login from './Login';
import Sidebar from './Sidebar';
import Home from './Home';
import { LoadingBlock } from './Spinner';
// Every page's CSS, eagerly and in order — see the file for why it must not travel with the page.
import './pageStyles';
import './App.css';
// Loaded after App.css and every page stylesheet so the phone layout wins ties.
import './mobile.css';

/* Pages are fetched when first opened. Every page used to be a static import, so the first visit —
 * the login screen included — downloaded and parsed every report, the tracking playground and the
 * video tooling before anything drew: one 5 MB script.
 *
 * A deploy replaces the hashed chunk files, so a tab opened before it asks for a chunk that no longer
 * exists. That is answered with one reload (which fetches the new index and its chunks), remembered
 * for the session so a chunk that is genuinely broken shows its error instead of reloading forever. */
const CHUNK_RELOAD_KEY = 'melbournecity_chunk_reload';
function lazyPage(load) {
  return lazy(() => load()
    .then((m) => { try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* private mode */ } return m; })
    .catch((err) => {
      let reloaded = true;
      try { reloaded = !!sessionStorage.getItem(CHUNK_RELOAD_KEY); } catch { /* private mode */ }
      if (!reloaded) {
        try { sessionStorage.setItem(CHUNK_RELOAD_KEY, '1'); } catch { /* private mode */ }
        window.location.reload();
        return new Promise(() => {});
      }
      throw err;
    }));
}

const TacticalXml = lazyPage(() => import('./TacticalXml'));
const Permissions = lazyPage(() => import('./Permissions'));

function PageLoading() {
  return (
    <div style={{ height: '100%', minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <LoadingBlock text="Opening…" />
    </div>
  );
}

// Blocks direct-URL access to a page the signed-in user isn't allowed to open.
function NoAccess() {
  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', textAlign: 'center',
      color: 'var(--text-tertiary)', gap: 8, padding: 24,
    }}>
      <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
        No access
      </div>
      <div style={{ fontSize: '0.8rem', maxWidth: 340 }}>
        You don't have permission to view this page. Contact an administrator if you
        need access.
      </div>
    </div>
  );
}

// Gate the routed content by the current path's access rule.
function AccessGate({ children }) {
  const { can } = useAuth();
  const location = useLocation();
  return can(location.pathname) ? children : <NoAccess />;
}

function MainApp() {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const toggleSidebar = useCallback(() => setIsCollapsed((c) => !c), []);

  return (
      <Router>
        <div className="app-container">
          {/* Ambient background glow */}
          <div className="ambient-glow">
            <div className="glow-orb-1" />
            <div className="glow-orb-2" />
          </div>

          {/* Film grain overlay */}
          <div className="film-grain" />

          <Sidebar isCollapsed={isCollapsed} toggleSidebar={toggleSidebar} />
          <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <AccessGate>
              <Suspense fallback={<PageLoading />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/tactical-xml" element={<TacticalXml />} />
                <Route path="/permissions" element={<Permissions />} />
                {/* Renamed pages keep their old address as a redirect — see pageRoutes.js. */}
                {PAGE_REDIRECTS.map(([from, to]) => (
                  <Route key={from} path={from} element={<Navigate to={to} replace />} />
                ))}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
            </AccessGate>
          </div>
        </div>
      </Router>
  );
}

// Shows the login screen until the user signs in, then the full app.
function Gate() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <MainApp /> : <Login />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
